import React, { useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from '@/components/modal/Modal';
import { UnsavedChangesModal } from '@/components/unsavedChanges/UnsavedChangesModal';
import { ModalProps } from '@/components/modal/useModal';
import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { Form } from '@/components/form/Form';
import { TextField } from '@/components/form/fields/text/TextInput';
import { TextAreaField } from '@/components/form/fields/text/TextAreaInput';
import { Card } from '@/components/sections/Card';
import { useCreateServiceRequest } from '@/services/api/hooks/serviceRequests';
import { useCreateCustomerAddress } from '@/services/api/hooks/address';
import { toast } from 'sonner';

import { AddressBookSelector } from '@/components/form/fields/address/AddressBookSelector';
import { SmartUserSelector } from '@/components/form/fields/SmartUserSelector';
import { AddressRequest } from '@/services/api/contracts/address';
import { DateField } from '@/components/form/fields/date/DateInput';
import { CopyFromUserAction } from '@/components/form/fields/CopyFromUserAction';
import { useDirtyFormGuard } from '@/hooks/useDirtyFormGuard';
import { isWeekend, isBefore, startOfDay, addDays } from 'date-fns';
import { useCompany } from '@/services/api/hooks/companies';
import { formatCurrency } from '@/utils/currency';

const isDateDisabled = (date: Date) => {
  if (isWeekend(date)) return true;
  
  let daysToAdd = 2;
  const cursor = new Date();
  while (daysToAdd >= 0) {
    cursor.setDate(cursor.getDate() + 1);
    if (!isWeekend(cursor)) {
      daysToAdd--;
    }
  }
  return isBefore(date, startOfDay(cursor));
};

const CheckoutFormSchema = z.object({
  fullName: z.string().optional(),
  workEmail: z.string().optional(),
  personalEmail: z.string().optional(),
  mobileNumber: z.string().optional(),
  customerReference: z.string().optional(),
  notes: z.string().optional(),
  address: AddressRequest.omit({ name: true }).extend({
    name: z.string().optional(),
    saveToAddressBook: z.boolean().optional()
  }),
  deliveryContactName: z.string().min(1, 'Required'),
  deliveryContactEmail: z.string().email('Invalid email'),
  deliveryContactMobile: z.string().min(1, 'Required'),
  isAsapExpedite: z.boolean().default(false),
  targetDate: z.date().optional()
});

type CheckoutFormFields = z.infer<typeof CheckoutFormSchema>;

export interface TechRefreshModalProps extends ModalProps {
  mspId: string;
  customerId: string;
  checkoutPayload: {
    requestedBy: string;
    requestType: 'OffboardUser' | 'DeployInventory' | 'AdvDeviceReplacement' | 'TechRefresh';
    customerReference: string;
    payload: any;
    runningTotal: number;
    devicesToBeCollected: string[];
    deployedHardware?: Array<{ name: string; quantity: number; sku: string }>;
    inferredServices: Array<{ rateCard?: any; quantity: number; labels?: string[], hasLifecyclePack?: boolean, isInventoryItem?: boolean, name?: string }>;
    cart: Record<string, number>;
    userContext?: {
      fullName?: string;
      workEmail?: string;
      personalEmail?: string;
      mobileNumber?: string;
      noCorporateEmail?: boolean;
    };
  } | null;
  uniqueUsers: any[];
  onSuccessCallback: () => void;
}

export const TechRefreshModal: React.FC<TechRefreshModalProps> = ({ 
  mspId, 
  customerId, 
  checkoutPayload, 
  uniqueUsers,
  onSuccessCallback, 
  ...props 
}) => {
  const [company] = useCompany(mspId, customerId);
  const createServiceRequest = useCreateServiceRequest();
  const createCustomerAddress = useCreateCustomerAddress();
  
  const methods = useForm<CheckoutFormFields>({
    resolver: zodResolver(CheckoutFormSchema),
    defaultValues: {
      fullName: checkoutPayload?.userContext?.fullName || '',
      workEmail: checkoutPayload?.userContext?.workEmail || '',
      personalEmail: checkoutPayload?.userContext?.personalEmail || '',
      mobileNumber: checkoutPayload?.userContext?.mobileNumber || '',
      customerReference: '',
      notes: ''
    }
  });



  const handleAddressChange = React.useCallback((addressPayload: any) => {
    methods.setValue('address', addressPayload, { shouldValidate: true, shouldDirty: true });
  }, [methods]);

  const onSubmit = async (data: CheckoutFormFields) => {
    if (!checkoutPayload) return;

    const finalPayload = JSON.parse(JSON.stringify(checkoutPayload.payload));

    const finalCustomerReference = data.customerReference?.trim() || finalPayload.requestContext.customerReference;
    finalPayload.requestContext.customerReference = finalCustomerReference;
    
    // Extract and strip identity properties from address
    const { companyId, partnerId, saveToAddressBook, ...cleanAddress } = (data.address || {}) as any;

    let finalAddress = { ...cleanAddress };

    if (saveToAddressBook) {
      try {
        const savedAddr = await createCustomerAddress.mutateAsync({
          ...cleanAddress,
          name: cleanAddress.name || 'Saved Address',
          mspId,
          customerId
        });
        const { id, companyId: cid, partnerId: pid, ...restSaved } = savedAddr as any;
        finalAddress = restSaved;
      } catch (e) {
        toast.error('Failed to save address to address book. Continuing with request...');
      }
    }

    const resolvedAddressName = finalAddress.name || 'Tech Refresh Address';

    // Inject logistics deeply into both deployment and collection
    const logisticsObject = {
      schedule: {
        requestedDate: data.targetDate ? data.targetDate.toISOString() : null,
        isAsapExpedite: data.isAsapExpedite
      },
      address: { ...finalAddress, name: resolvedAddressName },
      siteContact: {
        contactName: data.deliveryContactName,
        contactEmail: data.deliveryContactEmail === 'na' ? null : data.deliveryContactEmail,
        contactMobile: data.deliveryContactMobile
      },
      notes: data.notes
    };

    if (finalPayload.deployment) {
      finalPayload.deployment.logistics = logisticsObject;
    }
    if (finalPayload.collection) {
      finalPayload.collection.logistics = logisticsObject;
    }

    try {
      await createServiceRequest.mutateAsync({
        params: { mspId, customerId },
        body: {
          requestedBy: checkoutPayload.requestedBy,
          requestType: checkoutPayload.requestType,
          customerReference: finalCustomerReference,
          payload: finalPayload
        }
      });
      
      toast.success('Tech Refresh Logged Successfully');
      onSuccessCallback();
      props.close();
    } catch (e) {
      toast.error('Failed to log request. Please try again.');
    }
  };

  const { isDirty } = methods.formState;
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  useDirtyFormGuard(isDirty);

  const handleClose = () => {
    if (isDirty) {
      setShowUnsavedWarning(true);
    } else {
      props.close();
    }
  };

  const guardedProps = { ...props, close: handleClose };

  if (!checkoutPayload) return null;

  return (
    <>
      <Modal className="md:max-w-5xl lg:max-w-6xl flex flex-col h-[90vh]" {...guardedProps}>
      <Card className="w-full h-full flex flex-col overflow-hidden bg-white">
        <div className="p-8 pb-4 shrink-0">
          <Modal.Controls {...guardedProps}>
            <h1 className="text-2xl font-semibold">Tech Refresh</h1>
          </Modal.Controls>
        </div>
        
        <Form methods={methods} onSubmit={onSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto px-8 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              
              {/* Left Column: Summary */}
              <div className="space-y-6">
                <div className="flex flex-col border border-gray-200 rounded bg-gray-50 p-6 h-fit">
                  <h3 className="text-lg font-bold border-b pb-3 mb-4">Refresh Summary</h3>
                  
                  {checkoutPayload.devicesToBeCollected.length > 0 && (
                    <div className="mb-4 bg-white p-3 rounded border border-gray-200 text-sm">
                      <p className="font-semibold text-gray-800 mb-2 border-b pb-1">Hardware to Collect:</p>
                      <ul className="space-y-1 text-gray-600 list-disc list-inside">
                        {checkoutPayload.devicesToBeCollected.map((d, i) => (
                          <li key={i}>{d}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {checkoutPayload.deployedHardware && checkoutPayload.deployedHardware.length > 0 && (
                    <div className="mb-4 bg-white p-3 rounded border border-gray-200 text-sm">
                      <p className="font-semibold text-gray-800 mb-2 border-b pb-1">Hardware to Deploy:</p>
                      <ul className="space-y-1 text-gray-600 list-disc list-inside">
                        {checkoutPayload.deployedHardware.map((item, index) => (
                          <li key={`hw-dep-${index}`}>
                            <span className="font-medium">{item.quantity}x</span> {item.name} <span className="opacity-75 text-xs ml-1">({item.sku})</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="space-y-4 flex-1 mt-4">
                    {checkoutPayload.inferredServices.map((item: any, index: number) => {
                      const isInventoryItem = item.isInventoryItem;
                      return (
                        <div key={index} className="flex justify-between items-start text-sm border-b border-gray-100 last:border-b-0 pb-2">
                          <div className="pr-4">
                            <span className="font-medium">{item.quantity}x</span> {item.name || item.rateCard?.name}
                            {item.hasLifecyclePack && (
                              <span className="italic text-green-600 text-xs block mt-1">
                                Lifecycle Pack Detected<br />Costs will be adjusted accordingly
                              </span>
                            )}
                          </div>
                          <div className="font-medium whitespace-nowrap">
                            {isInventoryItem ? (
                              null
                            ) : (
                              formatCurrency(((item.rateCard?.price?.price || 0) / 100) * item.quantity, company?.region?.code)
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {checkoutPayload.inferredServices.length === 0 && (
                      <div className="text-gray-500 italic text-sm">No items selected.</div>
                    )}
                  </div>
                  
                  <div className="flex justify-between items-center pt-4 mt-6 border-t font-bold text-lg">
                    <span>Estimated Total</span>
                    <span>{formatCurrency(checkoutPayload.runningTotal, company?.region?.code)}</span>
                  </div>
                </div>

                <div className="border p-4 rounded space-y-4 bg-white">
                  <div>
                    <TextField id="customerReference" label="Customer/MSP Reference" placeholder="This will appear on the invoice" optional={true} />
                    <p className="text-xs text-gray-500 italic mt-1">If no reference is given, the user's name will be used</p>
                  </div>
                </div>

                <div className="border p-4 rounded space-y-4 bg-white">
                  <TextAreaField id="notes" label="Notes / Special Requirements" optional={true} />
                </div>
              </div>

              {/* Right Column: Logistics & Data Entry */}
              <div className="space-y-6">
                <SmartUserSelector 
                  methods={methods as any} 
                  uniqueUsers={uniqueUsers} 
                  onSelect={() => {}} 
                  isSimpleContactMode={true}
                  title="Collection & Delivery Contact"
                  headerAction={
                    <CopyFromUserAction 
                      control={methods.control} 
                      setValue={methods.setValue} 
                      getValues={methods.getValues} 
                      sourceNameField="fullName" 
                      sourcePrimaryEmailField="workEmail" 
                      sourceFallbackEmailField="personalEmail" 
                      sourceMobileField="mobileNumber"
                      targetNameField="deliveryContactName" 
                      targetEmailField="deliveryContactEmail" 
                      targetMobileField="deliveryContactMobile"
                    />
                  }
                  nameField="deliveryContactName"
                  corpEmailField="deliveryContactEmail"
                  mobileNumberField="deliveryContactMobile"
                  nameLabel="Contact Name"
                  corpEmailLabel="Contact Email"
                  mobileNumberLabel="Contact Mobile"
                  personalEmailOptional={false}
                  mobileNumberOptional={false}
                />

                <div className="border border-gray-200 p-4 rounded space-y-4 bg-white">
                  <h4 className="text-lg font-bold">Delivery & Collection Address</h4>
                  <AddressBookSelector 
                    mspId={mspId}
                    customerId={customerId}
                    onChange={handleAddressChange}
                  />
                  {methods.formState.errors.address && (
                    <p className="text-red-500 text-sm mt-1">Please select or provide a valid address.</p>
                  )}
                </div>

                <div className="border border-gray-200 p-4 rounded space-y-4 bg-white">
                  <h4 className="text-lg font-bold">Schedule</h4>
                  <div className="space-y-2">
                    <div className={methods.watch('isAsapExpedite') ? 'hidden' : ''}>
                      <DateField id="targetDate" label="Target Delivery Date" optional={true} excludePastDates={true} disabledPredicate={isDateDisabled} />
                    </div>
                    <div className="mt-2">
                      <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-700">
                        <input type="checkbox" {...methods.register('isAsapExpedite')} onChange={(e) => {
                          methods.setValue('isAsapExpedite', e.target.checked);
                          if (e.target.checked) {
                            methods.setValue('targetDate', addDays(new Date(), 1), { shouldValidate: true, shouldDirty: true });
                          } else {
                            methods.setValue('targetDate', undefined as any);
                            methods.clearErrors('targetDate');
                          }
                        }} className="w-4 h-4 rounded border-gray-300 text-primary-700" />
                        ASAP - Expedite
                      </label>
                      {methods.watch('isAsapExpedite') && (
                        <p className="text-xs italic text-amber-600 ml-6 mt-1">
                          This may incur additional charges
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>

          <div className="flex justify-end p-6 border-t bg-gray-50 shrink-0">
            <Button variant={ButtonVariant.Ghost} onClick={guardedProps.close} className="mr-4" disabled={createServiceRequest.isLoading}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant={ButtonVariant.Primary}
              disabled={createServiceRequest.isLoading}
            >
              {createServiceRequest.isLoading ? 'Submitting...' : 'Confirm & Submit'}
            </Button>
          </div>
        </Form>
      </Card>
    </Modal>
    <UnsavedChangesModal
      isOpen={showUnsavedWarning}
      open={() => setShowUnsavedWarning(true)}
      close={() => setShowUnsavedWarning(false)}
      onCancelCallback={() => setShowUnsavedWarning(false)}
      onConfirmCallback={() => {
        setShowUnsavedWarning(false);
        props.close();
      }}
      title="Abandon Request"
      message="Your request is partially complete. Are you sure you want to leave? Your progress will be lost."
      confirmText="Yes, Abandon"
      confirmColorClass="bg-secondary-600 hover:bg-secondary-700 text-white"
      cancelText="No, Continue"
      cancelColorClass="bg-primary-700 hover:bg-primary-800 text-white"
    />
    </>
  );
};
